import React, { useState } from 'react';
import { Dropdown } from '../ui/Dropdown';
import { Download, Upload, ArrowLeftRight, PieChart, ExternalLink, SlidersHorizontal, RotateCcw, GripVertical } from 'lucide-react';
import { assetHoldings, spotBalance, marginBalance, totalPortfolioValue } from '../../utils/portfolioData';
interface Balance {
  asset: string;
  symbol: string;
  logo: string;
  balance: number;
  avgPrice: string;
  currentPrice: number;
  estValue: number;
  upnl: string;
  upnlPercent: string;
  availBalance: number;
}
interface ColumnConfig {
  key: string;
  label: string;
  enabled: boolean;
  align: 'left' | 'right';
}
export function BalancesTable() {
  const [showSettings, setShowSettings] = useState(false);
  const [showSmallBalances, setShowSmallBalances] = useState(true);
  const [columns, setColumns] = useState<ColumnConfig[]>([{
    key: 'asset',
    label: 'Asset',
    enabled: true,
    align: 'left'
  }, {
    key: 'balance',
    label: 'Balance',
    enabled: true,
    align: 'right'
  }, {
    key: 'avgPrice',
    label: 'Avg. Price',
    enabled: true,
    align: 'right'
  }, {
    key: 'currentPrice',
    label: 'Current Price',
    enabled: true,
    align: 'right'
  }, {
    key: 'estValue',
    label: 'Est. Value',
    enabled: true,
    align: 'right'
  }, {
    key: 'upnl',
    label: 'UP&L',
    enabled: true,
    align: 'right'
  }, {
    key: 'upnlPercent',
    label: 'UP&L (%)',
    enabled: true,
    align: 'right'
  }, {
    key: 'availBalance',
    label: 'Avail. balance',
    enabled: false,
    align: 'right'
  }]);
  const toggleColumn = (key: string) => {
    setColumns(columns.map(col => col.key === key ? {
      ...col,
      enabled: !col.enabled
    } : col));
  };
  const resetColumns = () => {
    setColumns(columns.map(col => ({
      ...col,
      enabled: true
    })));
  };
  const visibleColumns = columns.filter(col => col.enabled);
  // Convert assetHoldings to Balance format
  const balances: Balance[] = assetHoldings.filter(asset => asset.spotAmount > 0).map(asset => ({
    asset: asset.name,
    symbol: asset.symbol,
    logo: asset.logo,
    balance: asset.spotAmount,
    avgPrice: '-',
    currentPrice: asset.price,
    estValue: asset.spotAmount * asset.price,
    upnl: '-',
    upnlPercent: '-',
    availBalance: asset.spotAmount
  }));
  return <div className="h-full overflow-auto relative" data-id="element-1281">
      {/* Header with Settings */}
      <div className="sticky top-0 bg-background-secondary z-20 border-b border-border-subtle" data-id="element-1282">
        <div className="flex items-center justify-between px-4 py-2" data-id="element-1283">
          <button className="text-xs text-text-secondary hover:text-text-primary transition-colors flex items-center gap-1" data-id="element-1284">
            <span data-id="element-1285">▼</span> Selected market
          </button>
          <button onClick={() => setShowSettings(!showSettings)} className="p-1.5 hover:bg-background-elevated rounded transition-colors text-text-secondary hover:text-text-primary" data-id="element-1286">
            <SlidersHorizontal size={16} data-id="element-1287" />
          </button>
        </div>

        {/* Settings Panel */}
        {showSettings && <div className="absolute right-4 top-12 w-80 bg-background-elevated border border-border-subtle rounded-xl shadow-2xl z-30 overflow-hidden max-h-[calc(100vh-200px)] flex flex-col" data-id="element-1288">
            <div className="p-4 border-b border-border-subtle flex-shrink-0" data-id="element-1289">
              <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wider mb-4" data-id="element-1290">
                Widget Settings
              </h3>

              <div className="flex items-center justify-between mb-4" data-id="element-1291">
                <span className="text-sm text-text-primary" data-id="element-1292">
                  Show small balances
                </span>
                <button onClick={() => setShowSmallBalances(!showSmallBalances)} className={`
                    relative w-11 h-6 rounded-full transition-all duration-200
                    ${showSmallBalances ? 'bg-brand-purple' : 'bg-background-secondary'}
                  `} data-id="element-1293">
                  <span className={`
                      absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white
                      transition-all duration-200 ease-out
                      ${showSmallBalances ? 'translate-x-5' : 'translate-x-0'}
                    `} data-id="element-1294" />
                </button>
              </div>
            </div>

            <div className="p-4 flex-1 overflow-y-auto" data-id="element-1295">
              <div className="flex items-center justify-between mb-3" data-id="element-1296">
                <h4 className="text-xs font-semibold text-text-tertiary uppercase tracking-wider" data-id="element-1297">
                  Columns
                </h4>
                <button onClick={resetColumns} className="text-xs text-brand-purple hover:text-brand-purple-light transition-colors flex items-center gap-1" data-id="element-1298">
                  <RotateCcw size={12} data-id="element-1299" />
                  Reset
                </button>
              </div>

              <div className="space-y-1" data-id="element-1300">
                {columns.map(column => <div key={column.key} className="flex items-center justify-between p-2 hover:bg-background-secondary rounded transition-colors group" data-id="element-1301">
                    <div className="flex items-center gap-3" data-id="element-1302">
                      <button onClick={() => toggleColumn(column.key)} className={`
                          w-5 h-5 rounded flex items-center justify-center transition-all
                          ${column.enabled ? 'bg-brand-purple text-white' : 'bg-background-secondary border border-border-default'}
                        `} data-id="element-1303">
                        {column.enabled && <svg width="12" height="12" viewBox="0 0 12 12" fill="none" data-id="element-1304">
                            <path d="M2 6l2.5 2.5L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1305" />
                          </svg>}
                      </button>
                      <span className="text-sm text-text-primary" data-id="element-1306">
                        {column.label}
                      </span>
                    </div>
                    <button className="opacity-0 group-hover:opacity-100 transition-opacity text-text-tertiary" data-id="element-1307">
                      <GripVertical size={16} data-id="element-1308" />
                    </button>
                  </div>)}
              </div>
            </div>
          </div>}
      </div>

      {/* Table */}
      <table className="w-full" data-id="element-1309">
        <thead className="sticky top-[41px] bg-background-secondary z-10" data-id="element-1310">
          <tr className="border-b border-border-subtle" data-id="element-1311">
            {visibleColumns.map(column => <th key={column.key} className={`px-4 py-3 text-xs font-medium text-text-tertiary uppercase tracking-wider ${column.align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-1312">
                {column.key === 'estValue' && <span className="inline-flex items-center gap-1" data-id="element-1313">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" data-id="element-1314">
                      <path d="M6 2L7 6l4 1-4 1-1 4-1-4-4-1 4-1z" data-id="element-1315" />
                    </svg>
                    {column.label}
                  </span>}
                {column.key !== 'estValue' && column.label}
              </th>)}
            <th className="px-4 py-3" data-id="element-1316"></th>
          </tr>
        </thead>
        <tbody data-id="element-1317">
          {balances.map((balance, idx) => <tr key={idx} className="border-b border-border-subtle hover:bg-background-elevated transition-colors group" data-id="element-1318">
              {visibleColumns.map(column => {
            if (column.key === 'asset') {
              return <td key={column.key} className="px-4 py-3" data-id="element-1319">
                      <div className="flex items-center gap-2" data-id="element-1320">
                        <img src={balance.logo} alt={balance.symbol} className="w-6 h-6 object-contain" data-id="element-1321" />
                        <div data-id="element-1322">
                          <div className="font-medium text-text-primary text-sm" data-id="element-1323">
                            {balance.asset}
                          </div>
                          <div className="text-xs text-text-secondary" data-id="element-1324">
                            {balance.symbol}
                          </div>
                        </div>
                      </div>
                    </td>;
            }
            if (column.key === 'balance') {
              return <td key={column.key} className="px-4 py-3 text-right" data-id="element-1325">
                      <div className="font-mono text-sm text-text-primary" data-id="element-1326">
                        {balance.balance.toLocaleString('en-US', {
                    minimumFractionDigits: balance.symbol === 'USDT' ? 2 : 8,
                    maximumFractionDigits: balance.symbol === 'USDT' ? 2 : 8
                  })}
                        <span className="text-text-secondary text-xs ml-1" data-id="element-1327">
                          {balance.symbol}
                        </span>
                      </div>
                    </td>;
            }
            if (column.key === 'currentPrice') {
              return <td key={column.key} className="px-4 py-3 text-right" data-id="element-1328">
                      <div className="font-mono text-sm text-text-primary" data-id="element-1329">
                        {balance.currentPrice.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                        <span className="text-text-secondary text-xs ml-1" data-id="element-1330">
                          USD
                        </span>
                      </div>
                    </td>;
            }
            if (column.key === 'estValue') {
              return <td key={column.key} className="px-4 py-3 text-right" data-id="element-1331">
                      <div className="font-mono text-sm text-text-primary font-medium" data-id="element-1332">
                        {balance.estValue.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                        <span className="text-text-secondary text-xs ml-1" data-id="element-1333">
                          USD
                        </span>
                      </div>
                    </td>;
            }
            if (column.key === 'availBalance') {
              return <td key={column.key} className="px-4 py-3 text-right" data-id="element-1334">
                      <div className="font-mono text-sm text-text-primary" data-id="element-1335">
                        {balance.availBalance.toLocaleString('en-US', {
                    minimumFractionDigits: balance.symbol === 'USDT' ? 2 : 8,
                    maximumFractionDigits: balance.symbol === 'USDT' ? 2 : 8
                  })}
                        <span className="text-text-secondary text-xs ml-1" data-id="element-1336">
                          {balance.symbol}
                        </span>
                      </div>
                    </td>;
            }
            const value = balance[column.key as keyof Balance];
            return <td key={column.key} className={`px-4 py-3 ${column.align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-1337">
                    <span className="text-sm text-text-tertiary" data-id="element-1338">-</span>
                  </td>;
          })}
              <td className="px-4 py-3 text-right" data-id="element-1339">
                <Dropdown value="actions" options={[{
              value: 'deposit',
              label: 'Deposit',
              icon: <Download size={16} data-id="element-1341" />
            }, {
              value: 'withdraw',
              label: 'Withdraw',
              icon: <Upload size={16} data-id="element-1342" />
            }, {
              value: 'convert',
              label: 'Convert',
              icon: <ArrowLeftRight size={16} data-id="element-1343" />
            }, {
              value: 'allocate',
              label: 'Allocate',
              icon: <PieChart size={16} data-id="element-1344" />
            }, {
              value: 'transfer',
              label: 'Transfer to futures',
              icon: <Download size={16} data-id="element-1345" />
            }, {
              value: 'market',
              label: 'Go to market',
              icon: <ExternalLink size={16} data-id="element-1346" />
            }]} onChange={value => console.log('Action:', value)} variant="actions" data-id="element-1340" />
              </td>
            </tr>)}
        </tbody>
      </table>

      {/* Click outside to close settings */}
      {showSettings && <div className="fixed inset-0 z-20" onClick={() => setShowSettings(false)} data-id="element-1347" />}
    </div>;
}